class VanillaBgNoise {
  constructor(selector = ".js-noise-bg", options = {}, root = document) {
    this.selector = selector;
    this.root = root;

    this.frameCount = options.frameCount || 10;
    this.fps = options.fps || 25;
    this.resizeDelay = options.resizeDelay || 200;

    this.instances = [];
    this.isDestroyed = false;

    this.init();
  }

  init() {
    const canvases = this.getCanvases();

    if (!canvases.length) return;

    canvases.forEach((canvas) => {
      const instance = this.createInstance(canvas);

      if (instance) {
        this.instances.push(instance);
      }
    });
  }

  getCanvases() {
    if (this.root && typeof this.root.querySelectorAll === "function") {
      return Array.from(this.root.querySelectorAll(this.selector));
    }

    return Array.from(document.querySelectorAll(this.selector));
  }

  createInstance(canvas) {
    const ctx = canvas.getContext("2d");

    if (!ctx) return null;

    const instance = {
      canvas,
      ctx,
      width: 0,
      height: 0,
      noiseData: [],
      frame: 0,
      loopTimeout: null,
      animationFrameId: null,
      resizeThrottle: null,
      handleResize: null,
    };

    this.setup(instance);
    this.bindResize(instance);

    return instance;
  }

  createNoise(instance) {
    const { ctx, width, height, noiseData } = instance;

    if (!ctx || !width || !height) return;

    const idata = ctx.createImageData(width, height);
    const buffer32 = new Uint32Array(idata.data.buffer);
    const len = buffer32.length;

    for (let i = 0; i < len; i++) {
      const random = Math.random();

      if (random < 0.05) {
        buffer32[i] = 0x080000ff;
      } else if (random < 0.1) {
        buffer32[i] = 0x0800ff00;
      } else if (random < 0.15) {
        buffer32[i] = 0x08ff0000;
      } else if (random < 0.2) {
        buffer32[i] = 0xccdddddd;
      }
    }

    noiseData.push(idata);
  }

  paintNoise(instance) {
    if (this.isDestroyed) return;

    const { ctx, noiseData } = instance;

    if (!ctx || !noiseData.length) return;

    if (instance.frame >= this.frameCount - 1) {
      instance.frame = 0;
    } else {
      instance.frame++;
    }

    ctx.putImageData(noiseData[instance.frame], 0, 0);
  }

  loop(instance) {
    if (this.isDestroyed) return;

    this.paintNoise(instance);

    instance.loopTimeout = window.setTimeout(() => {
      if (this.isDestroyed) return;

      instance.animationFrameId = window.requestAnimationFrame(() => {
        this.loop(instance);
      });
    }, 1000 / this.fps);
  }

  setup(instance) {
    if (this.isDestroyed) return;

    this.clearLoop(instance);

    const { canvas } = instance;
    const rect = canvas.getBoundingClientRect();

    instance.width = Math.ceil(rect.width || window.innerWidth);
    instance.height = Math.ceil(rect.height || window.innerHeight);

    canvas.width = instance.width;
    canvas.height = instance.height;

    instance.noiseData = [];
    instance.frame = 0;

    for (let i = 0; i < this.frameCount; i++) {
      this.createNoise(instance);
    }

    this.loop(instance);
  }

  bindResize(instance) {
    instance.handleResize = () => {
      if (this.isDestroyed) return;

      window.clearTimeout(instance.resizeThrottle);

      instance.resizeThrottle = window.setTimeout(() => {
        if (this.isDestroyed) return;

        this.setup(instance);
      }, this.resizeDelay);
    };

    window.addEventListener("resize", instance.handleResize, false);
  }

  clearLoop(instance) {
    if (instance.loopTimeout) {
      window.clearTimeout(instance.loopTimeout);
      instance.loopTimeout = null;
    }

    if (instance.animationFrameId) {
      window.cancelAnimationFrame(instance.animationFrameId);
      instance.animationFrameId = null;
    }
  }

  destroy() {
    this.isDestroyed = true;

    this.instances.forEach((instance) => {
      if (!instance) return;

      this.clearLoop(instance);

      if (instance.resizeThrottle) {
        window.clearTimeout(instance.resizeThrottle);
        instance.resizeThrottle = null;
      }

      if (instance.handleResize) {
        window.removeEventListener("resize", instance.handleResize, false);
        instance.handleResize = null;
      }

      if (instance.ctx && instance.canvas) {
        instance.ctx.clearRect(
          0,
          0,
          instance.canvas.width,
          instance.canvas.height,
        );
      }

      instance.noiseData = [];
      instance.canvas = null;
      instance.ctx = null;
    });

    this.instances = [];
  }
}

export default VanillaBgNoise;
